<template>
  <div class="property-list-table">
    <el-table
      ref="table"
      row-key="id"
      v-loading="loading"
      :data="rows"
      @sort-change="doChangeSort"
      @row-click="onRowClick"
    >
      <!-- Name -->
      <el-table-column 
        :resizable="false" 
        :label="fields.name.label" 
        :prop="fields.name.name" 
        sortable="custom" 
        min-width="120"
      >
        <template slot-scope="scope">
          <div
            class="list__avatar"
            :style="{ backgroundImage: getPropertyAvatar(scope.row) }"
          />
          <el-tooltip
            :disabled="!hasPermissionToAuditLogs"
            effect="dark"
            :content="scope.row.id"
            placement="bottom"
          >
            <span
              class="list__avatar__name"
              v-text="presenter(scope.row, 'name')"
            />
          </el-tooltip>
        </template>
      </el-table-column>

      <!-- Sport -->
      <el-table-column
        :resizable="false" 
        :label="fields.sport.label" 
        :prop="fields.sport.name" 
        min-width="50"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'sport') }}</template>
      </el-table-column>

      <!-- Type -->
      <el-table-column 
        :resizable="false" 
        :label="fields.type.label" 
        :prop="fields.type.name" 
        min-width="60"
      >
        <template slot-scope="scope"> {{ presenter(scope.row, 'type') }}</template>
      </el-table-column>

      <!-- Managers -->
      <el-table-column 
        :resizable="false" 
        :label="fields.managers.label" 
        :prop="fields.managers.name" 
        min-width="100"
      >
        <template slot-scope="scope">
          <app-i18n
            v-if="!scope.row.managers || !scope.row.managers.length"
            code="common.none"
          />
          <span
            v-else-if="scope.row.managers.length === 1"
            v-text="scope.row.managers[0].fullName || scope.row.managers[0].email"
          />
          <el-tooltip
            v-else
            effect="dark"
            :content="scope.row.managers.map(manager => manager.fullName || manager.email).join('\n')"
            placement="bottom"
          >
            <app-i18n code="entities.property.list.multipleManagers" />
          </el-tooltip>
        </template>
      </el-table-column>

      <!-- Access -->
      <!-- <el-table-column :resizable="false" :label="fields.access.label" :prop="fields.access.name" :width="160">
        <template slot-scope="scope">
          
          <div class="property-list-table__access">

            <span style="float: left; font-size: 18px;">
              <i class="icon-radio-button-fill" :style="getTagStyle('access', scope.row.access)" />
            </span>
            <span
              style="float: left; padding-left: 10px; color: #8492a6; font-size: 13px"
              v-text="presenter(scope.row, 'access')"
            />

          </div>
        </template>
      </el-table-column> -->

      <!-- Actions -->
      <el-table-column 
        :fixed="isMobile? undefined : 'right'" 
        align="center" 
        min-width="60"
      >
        <template slot-scope="scope">
          <div class="table-actions">
            <router-link :to="`/property/${scope.row.id}/edit`" v-if="hasPermissionToEdit">
              <el-button
                icon="icon-pen"
                circle
              />
            </router-link>

            <el-button
              :disabled="destroyLoading"
              @click="doDestroyWithConfirm(scope.row.id)"
              circle
              v-if="hasPermissionToDestroy"
              icon="icon-trash"
            />
          </div>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- Alert Destroy-->
    <custom-alert
      :visible.sync="destroyAlertData.visible"
      :type="destroyAlertData.type"
      :title="destroyAlertData.title"
      :description="destroyAlertData.description"
      :confirmText="destroyAlertData.confirmText"
      :onConfirmFn="destroyAlertData.onConfirmFn"
    />

    <div class="el-pagination-wrapper">
      <el-pagination
        :current-page="pagination.currentPage || 1"
        :disabled="loading"
        :layout="paginationLayout"
        :total="count"
        @current-change="doChangePaginationCurrentPage"
        @size-change="doChangePaginationPageSize"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import { AuditLogPermissions } from '@modules/audit-log/audit-log-permissions';
import { PropertyModel } from '@modules/property/property-model';
import { mapGetters, mapActions } from 'vuex';
import { PropertyPermissions } from '@modules/property/property-permissions';
import { propertyStatusTagType } from '@modules/property/property-enum-status';
import { propertyAccessTagType } from '@modules/property/property-enum-access';
import { i18n } from '@shared/i18n';
const { fields } = PropertyModel;
import manageType from '@shared/manage/manage-enum';
import TagsFactory from '@shared/factories/tags-factory';

export default {
  name: 'app-property-list-table',

  mounted() {
    this.doFetch();
    // this.doMountTable(this.$refs.table);
  },

  data() {
    return {
      destroyAlertData: {
        visible: false,
        type: 'delete',
        title: i18n('common.areYouSure'),
        description: '',
        confirmText: i18n('common.yes'),
        onConfirmFn: () => {},
      }
    }
  },
  computed: {
    ...mapGetters({
      rows: 'property/list/rows',
      count: 'property/list/count',
      loading: 'property/list/loading',
      pagination: 'property/list/pagination',
      isMobile: 'layout/isMobile',
      currentUser: 'auth/currentUser',
      destroyLoading: 'property/destroy/loading',
      paginationLayout: 'layout/paginationLayout',
      currentManage: 'auth/manage/currentManage',
    }),

    hasPermissionToAuditLogs() {
      return new AuditLogPermissions(this.currentUser).read;
    },

    hasPermissionToEdit() {
      return new PropertyPermissions(this.currentUser).edit;
    },

    hasPermissionToDestroy() {
      return new PropertyPermissions(this.currentUser)
        .destroy;
    },

    fields() {
      return fields;
    },
  },

  methods: {
    ...mapActions({
      doChangeSort: 'property/list/doChangeSort',
      doChangePaginationCurrentPage: 'property/list/doChangePaginationCurrentPage',
      doChangePaginationPageSize: 'property/list/doChangePaginationPageSize',
      doDestroy: 'property/destroy/doDestroy',
      doChangeManage: 'auth/manage/doChangeManage',
      doChangeManageFromProperty: 'auth/manage/doChangeManageFromProperty',
      doFetch: 'property/list/doFetch',
    }),

    getTagStyle(keyEnum, valueEnum) {
      const style = new TagsFactory({
        entityName: 'common',
      }).getTagStyle({
        keyEnum: keyEnum,
        valueEnum: valueEnum,
      });
      return style;
    },

    presenter(row, fieldName) {
      return PropertyModel.presenter(row, fieldName);
    },

    async doDestroyWithConfirm(id) {
      this.destroyAlertData.visible = true;

      this.destroyAlertData.onConfirmFn = () => { 
        this.destroyAlertData.visible = false;
        this.doDestroy(id);
      };
    },

    /**
     * If numMaxSubproperty is one then redirect to the subproperty
     */
    async doManage(property) {
      this.doChangeManageFromProperty({
        manage: {
          ...this.currentManage,
          type: manageType.values.PROPERTY,
          property,
          propertyId: property.id,
          model: property,
          path: '/property'
        }
      })
    },
    onRowClick(row, column) {
      if (!('label' in column)) return;
      this.doManage(row);
    },
    propertyStatusTagType(status) {
      return propertyStatusTagType(status);
    },
    propertyAccessTagType(status) {
      return propertyAccessTagType(status);
    },
    getPropertyAvatar(row) {
      if (!('logos' in row)) return null;
      if (!(row.logos instanceof Array)) return null;
      if (!row.logos.length) return null;

      return `url("${row.logos[0].publicUrl}")`;
    },
  },
};
</script>

<style lang="scss" scoped>
.property-list-table {
  &.el-pagination-wrapper--show {
    display: initial;
  }
  &__access {
    display: grid;
    grid-template-areas: 'icon text';
    justify-content: center;
    align-items: center;
    padding: 4px 0;
    background-color: #ffffff;
    box-shadow: 0 2px 5px 0 rgba(223, 223, 223, 0.7);
    border-radius: 7.5px;
  }
}
</style>
